import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, TrendingUp, Store, Route as RouteIcon, AlertOctagon, AlertTriangle, CheckCircle2, Gauge } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { api } from "@/lib/api";

function SlaCard({ testid, label, value, accent, sub, icon: Icon }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E2E8F0] rounded-sm p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-500">{label}</span>
        <Icon size={15} style={{ color: accent }} />
      </div>
      <div className="mt-2 font-mono text-3xl font-light tabular-nums" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-1 font-mono">{sub}</div>}
    </div>
  );
}

function Bar({ pct, color }) {
  return (
    <div className="h-1.5 w-full bg-slate-100 rounded-sm overflow-hidden">
      <div className="h-full rounded-sm" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export function PerformancePanel({ runId, slaDays, setSlaDays }) {
  const [groupBy, setGroupBy] = useState("store");

  const { data: analytics, isLoading } = useQuery({
    queryKey: ["analytics", runId, slaDays, groupBy],
    queryFn: async () => (await api.get(`/recon/runs/${runId}/analytics`, { params: { sla_days: slaDays, group_by: groupBy } })).data,
    enabled: !!runId,
  });

  const { data: trend } = useQuery({
    queryKey: ["trend", slaDays],
    queryFn: async () => (await api.get("/recon/analytics/trend", { params: { sla_days: slaDays } })).data,
  });

  const sla = analytics?.sla_summary || {};
  const groups = analytics?.groups || [];
  const series = (trend?.series || []).map((s, i) => ({
    ...s, name: `#${i + 1}`,
  }));

  return (
    <div className="space-y-5" data-testid="performance-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Gauge size={18} /> Desempeño & SLA
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex rounded-sm border border-slate-300 overflow-hidden">
            {[3, 7, 14, 30, 60, 90, 120].map((d) => (
              <button key={d} data-testid={`perf-sla-${d}`} onClick={() => setSlaDays(d)}
                className={`px-3 h-9 text-sm ${slaDays === d ? "bg-[#0F172A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {d}d
              </button>
            ))}
          </div>
          <div className="flex rounded-sm border border-slate-300 overflow-hidden">
            <button data-testid="perf-group-store" onClick={() => setGroupBy("store")}
              className={`px-3 h-9 text-sm flex items-center gap-1 ${groupBy === "store" ? "bg-[#2563EB] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <Store size={14} /> Tienda
            </button>
            <button data-testid="perf-group-route" onClick={() => setGroupBy("route")}
              className={`px-3 h-9 text-sm flex items-center gap-1 ${groupBy === "route" ? "bg-[#2563EB] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <RouteIcon size={14} /> Ruta
            </button>
          </div>
        </div>
      </div>

      {/* SLA summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SlaCard testid="sla-fuera" label="Fuera de SLA" value={sla.fuera_sla ?? 0} accent="#BE123C" icon={AlertOctagon}
          sub={`umbral ${slaDays} días`} />
        <SlaCard testid="sla-riesgo" label="En riesgo" value={sla.en_riesgo ?? 0} accent="#D97706" icon={AlertTriangle}
          sub="próximos 3 días" />
        <SlaCard testid="sla-en-sla" label="En SLA" value={sla.en_sla ?? 0} accent="#059669" icon={CheckCircle2}
          sub="a tiempo" />
        <SlaCard testid="sla-pct" label="% En tiempo" value={`${sla.pct_en_tiempo ?? 0}%`} accent="#2563EB" icon={Gauge}
          sub={`${sla.total_open ?? 0} entregas abiertas`} />
      </div>

      {/* Trend */}
      <div className="bg-white border border-[#E2E8F0] rounded-sm p-4" data-testid="performance-trend">
        <div className="flex items-center gap-2 text-slate-700 mb-3">
          <TrendingUp size={16} /><span className="text-xs uppercase tracking-[0.05em] font-semibold">Tendencia entre corridas</span>
        </div>
        {series.length < 2 ? (
          <p className="text-sm text-slate-400 py-8 text-center">Sube más reportes para ver la tendencia histórica.</p>
        ) : (
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={series} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip contentStyle={{ borderRadius: 2, fontSize: 12, border: "1px solid #E2E8F0" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="pct_conciliado" name="% Conciliado" stroke="#059669" strokeWidth={2} dot />
                <Line type="monotone" dataKey="pct_diferencia" name="% Diferencia" stroke="#DC2626" strokeWidth={2} dot />
                <Line type="monotone" dataKey="fuera_sla" name="Fuera de SLA" stroke="#BE123C" strokeWidth={2} strokeDasharray="4 2" dot />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Group table */}
      <div className="bg-white border border-[#E2E8F0] rounded-sm" data-testid="performance-groups">
        <div className="overflow-auto thin-scroll">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="bg-slate-50 border-b border-[#E2E8F0] text-left">
                {[groupBy === "store" ? "Tienda" : "Ruta", "Total", "Conciliado", "Diferencia", "Pendiente", "Fuera SLA", "Riesgo", "Transfer/Reg", "% Conciliado"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={9} className="py-14 text-center text-slate-400"><Loader2 className="animate-spin inline" size={20} /></td></tr>
              ) : groups.length === 0 ? (
                <tr><td colSpan={9} className="py-14 text-center text-slate-400 text-sm">Sin datos</td></tr>
              ) : groups.map((g) => (
                <tr key={g.key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`perf-row-${g.key}`}>
                  <td className="px-3 py-2.5 font-medium text-slate-800 max-w-[260px] truncate">{g.key}</td>
                  <td className="px-3 py-2.5 font-mono text-slate-700">{g.total}</td>
                  <td className="px-3 py-2.5 font-mono text-emerald-700">{g.conciliado}</td>
                  <td className="px-3 py-2.5 font-mono text-red-700">{g.diferencia}</td>
                  <td className="px-3 py-2.5 font-mono text-amber-700">{g.pendiente_storis}</td>
                  <td className="px-3 py-2.5 font-mono text-[#BE123C] font-semibold">{g.fuera_sla}</td>
                  <td className="px-3 py-2.5 font-mono text-amber-600">{g.en_riesgo}</td>
                  <td className="px-3 py-2.5 font-mono text-slate-500">{g.transfer}/{g.regular}</td>
                  <td className="px-3 py-2.5 w-[140px]">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-600 w-9">{g.pct_conciliado}%</span>
                      <Bar pct={g.pct_conciliado} color="#059669" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
